import React, { useState, useEffect } from 'react';
import '../style/Register.css';
import { signup } from '../../../api/reg';
import logo from '../../../app_icon.svg';
import { useNavigate, useLocation } from 'react-router-dom';
import FloatingLabelInput from '../../../common/components/FloatingLabelInput';
import PhoneInput from '../../../common/components/PhoneInput';
import { t } from 'i18next';
import monitoringService from '../../../services/monitoringService';

const Register = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const userType = location.state?.userType || 'individual';

    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [bin, setBin] = useState('');
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [isAgreed, setIsAgreed] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [selectedCountry, setSelectedCountry] = useState(null);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleBackClick = () => {
        navigate('/sign');
    };

    const validateEmail = (emailValue) => {
        if (!emailValue.trim()) {
            return ''; // Email is optional
        }
        
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailValue)) {
            return t('invalidEmailFormat');
        }
        
        const allowedDomains = ['icloud.com', 'gmail.com', 'mail.ru'];
        const domain = emailValue.split('@')[1]?.toLowerCase();
        
        if (!allowedDomains.includes(domain)) {
            return t('invalidEmailDomain');
        }
        
        return '';
    };

    const handleEmailChange = (e) => {
        const emailValue = e.target.value;
        setEmail(emailValue);
        setEmailError(validateEmail(emailValue));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!name.trim()) {
            setErrorMessage(t('enter_full_name'));
            return;
        }

        if (!phone.trim()) {
            setErrorMessage(t('enter_phone'));
            return;
        }

        if (userType === 'business' && !bin.trim()) {
            setErrorMessage(t('enter_bin'));
            return;
        }

        // Validate email if provided
        if (email.trim()) {
            const emailValidationError = validateEmail(email);
            if (emailValidationError) {
                setErrorMessage(emailValidationError);
                return;
            }
        }

        try {
            // Get country code from selected country or default to +7
            const countryCode = selectedCountry?.code || '+7';
            const sanitizedPhoneNumber = phone.replace(/\D/g, '');
            const formattedPhoneNumber = `${countryCode}${sanitizedPhoneNumber}`;

            const data = await signup(name, formattedPhoneNumber, userType, bin, email.trim() || null);

            // Track registration attempt (will be updated to success in SmsCode)
            monitoringService.trackRegistration(formattedPhoneNumber, false, null).catch(err => {
                console.warn('Failed to track registration attempt:', err);
            });

            navigate('/sms-code', {
                state: {
                    taskId: data.links[0].href.split('/').pop(),
                    phoneNumber: formattedPhoneNumber,
                    typeAuth: 'signup',
                },
            });
        } catch (error) {
            // Display error message directly from backend
            const errorMessage = error.message || 'Ошибка при регистрации пользователя';
            // Track registration failure
            const countryCode = selectedCountry?.code || '+7';
            const sanitizedPhoneNumber = phone.replace(/\D/g, '');
            const formattedPhoneNumber = `${countryCode}${sanitizedPhoneNumber}`;
            monitoringService.trackRegistration(formattedPhoneNumber, false, errorMessage).catch(err => {
                console.warn('Failed to track registration failure:', err);
            });
            setErrorMessage(errorMessage);
            console.error("Registration error:", errorMessage);
        }

    };

    return (
        <div className="sign_page">
            <div className="left_section"></div>

            <div className="right_section">
                <div className="right_section_header">
                    <button onClick={handleBackClick} className="back_button">
                        <i className="back_icon fa-solid fa-chevron-left"></i>
                        {isMobile ? t('signUp') : t('back')}
                    </button>

                    {!isMobile &&
                        <div className="logo_container">
                            <img src={logo} alt="Agroland" className="logo" />
                        </div>
                    }
                </div>
                <div>
                    {!isMobile && <h2 className="title">{t('signUp')}</h2>}
                    <form onSubmit={handleSubmit} className="register_form">
                        {isMobile &&
                            <div className="logo_container">
                                <img src={logo} alt="Logo" className="form_logo" />
                            </div>
                        }

                        {userType === 'business' && (
                            <FloatingLabelInput
                                label={t('companyId')}
                                value={bin}
                                onChange={(e) => setBin(e.target.value)}
                            />
                        )}
                        <FloatingLabelInput
                            label={userType === 'business' ? t('companyName') : t('nameLabel')}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                        <PhoneInput
                            label={t('enter_phone')}
                            value={phone}
                            onChange={(e) => {
                                // Store only the phone number without country code
                                setPhone(e.target.value);
                            }}
                            onCountryChange={setSelectedCountry}
                        />
                        <FloatingLabelInput
                            label={t('enter_email') + ' (' + t('optional') + ')'}
                            value={email}
                            onChange={handleEmailChange}
                            type="email"
                        />
                        {emailError && <div className="error_message">{emailError}</div>}
                        {email && !emailError && (
                            <p className="email_hint">{t('emailHint')}</p>
                        )}
                        <div className="consent_section">
                            <input
                                type="checkbox"
                                id="consent"
                                checked={isAgreed}
                                onChange={() => setIsAgreed(!isAgreed)}
                            />
                            <label htmlFor="consent">
                                <a 
                                    href="/aboutus#policy" 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="consent_link"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {t('user_access').split(',')[0].trim()}
                                </a>
                                {', '}
                                <a 
                                    href="/aboutus#terms" 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="consent_link"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {t('user_access').split(',')[1]?.trim() || t('user_access').split(',')[1]}
                                </a>
                            </label>
                        </div>
                        {errorMessage && <div className="error_message">{errorMessage}</div>}
                        <button type="submit" className="submit_button" disabled={!isAgreed}>
                            {t('next')}
                        </button>
                    </form>
                </div>
                <div></div>
            </div>
        </div>
    );
};

export default Register;